/**
 * ReminderPopup — the single, series-aware reminder popup used everywhere
 * (exit-intent + 20s timer on the 6 workshop detail pages, and the
 * /workshops overview "Remind me" link). One design; the 6 series differ
 * only by props (icon, title, dates), never by separate components.
 *
 * Built from the original RemindMeModal as its starting point (same chrome,
 * brand calendar icons, copy-button styling), extended to:
 *   - resolve the soonest BOOKABLE session via the shared resolveReminderTarget
 *     (shows a live countdown when the soonest session is < 24h away and
 *     defaults the reminder to the next session),
 *   - a two-step flow: Step 1 collects first name + email; Step 2 asks the
 *     visitor to confirm their email to unlock the reward (a discount + the
 *     free recipe generator), and offers an opt-in add-to-calendar block
 *     below the inbox-check copy (clicking a provider opens Google / Outlook
 *     in a new tab or downloads the .ics for Apple).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import {
  WORKSHOP_SHORT_NAMES,
  workshopKeyFromString,
} from "@/lib/workshop-icons";
import { resolveReminderTarget, type ReminderSession } from "@shared/reminder-target";
import { REMINDER_REWARD_PERCENT, REMINDER_REWARD_CODE } from "@shared/reminder-reward";
import { useBodyScrollLock } from "@/lib/body-scroll-lock";
import { getLeadAttribution } from "@/lib/lead-attribution";

/* ─── Brand calendar icons (static SVG assets) ───────────────────────── */
function GoogleCalendarBrandIcon({ size = 28 }: { size?: number }) {
  return (
    <img src="/images/calendar-providers/google-calendar.svg" alt="" width={size} height={size} style={{ width: size, height: size, display: "block" }} aria-hidden="true" />
  );
}
function OutlookBrandIcon({ size = 28 }: { size?: number }) {
  return (
    <img src="/images/calendar-providers/outlook.svg" alt="" width={size} height={size} style={{ width: size, height: size, display: "block" }} aria-hidden="true" />
  );
}
function AppleCalendarBrandIcon({ size = 28 }: { size?: number }) {
  return (
    <img src="/images/calendar-providers/apple-calendar.svg" alt="" width={size} height={size} style={{ width: size, height: size, display: "block" }} aria-hidden="true" />
  );
}

/** A session for the popup — structurally compatible with ReminderSession. */
export interface ReminderPopupSession extends ReminderSession {
  timezone: string;
}

export interface ReminderPopupProps {
  open: boolean;
  onClose: () => void;
  /** Series slug, used for icon + server target resolution. */
  seriesSlug: string | null;
  /** Display title of the series. */
  seriesTitle: string;
  /** All upcoming sessions for the series (incl. sold-out; filtered here). */
  sessions: ReminderPopupSession[];
  /** Page the popup fired on, for analytics + audience routing. */
  pageSlug?: string;
  /** Which surface opened it. */
  source?: "exit_popup" | "overview_link";
  /**
   * Called once when the visitor successfully submits their email.
   * The parent uses this to write a permanent per-device suppression key to
   * localStorage so the popup never fires again on this device for this series.
   */
  onSubmitSuccess?: () => void;
}

type Provider = "google" | "outlook" | "apple";
type Step = "form" | "confirm";
type FormState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Calendar URL set returned by the reminder optin endpoint. */
type CalendarUrls = { google: string; outlook: string; ics: string };

function downloadIcs(filename: string, body: string) {
  const blob = new Blob([body], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Live countdown to a target Date. Returns null once the target passes. */
function useCountdown(target: Date | null): { d: number; h: number; m: number; s: number } | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [target?.getTime()]);
  if (!target) return null;
  const diff = target.getTime() - now;
  if (diff <= 0) return null;
  const s = Math.floor(diff / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

export function ReminderPopup({ open, onClose, seriesSlug, seriesTitle, sessions, pageSlug, source, onSubmitSuccess }: ReminderPopupProps) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<Step>("form");
  const [state, setState] = useState<FormState>({ kind: "idle" });
  const [calendarUrls, setCalendarUrls] = useState<CalendarUrls | null>(null);
  const [isNarrow, setIsNarrow] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.matchMedia("(max-width: 639px)").matches,
  );
  // Short viewports (e.g. iPhone 13 at 390x664): shrink the hero photo and
  // tighten vertical rhythm so the submit button is visible without scrolling.
  const [isShort, setIsShort] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.matchMedia("(max-height: 700px)").matches,
  );

  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Prevents double-submit via rapid Enter keypresses before the loading state re-renders.
  const submittingRef = useRef(false);

  const seriesKey = workshopKeyFromString(seriesSlug ?? seriesTitle);
  const shortTitle = WORKSHOP_SHORT_NAMES[seriesKey] || seriesTitle;

  // Resolve the target session(s). Recomputed when the session list changes.
  const { reminderTarget, countdownTarget } = useMemo(
    () => resolveReminderTarget(sessions, new Date()),
    [sessions],
  );

  const countdownDate = countdownTarget ? new Date(countdownTarget.startDatetime) : null;
  const countdown = useCountdown(open ? countdownDate : null);

  // The date the reminder is actually for (the next session >= 24h out),
  // shown in the eyebrow so it's clear which session we'll remind about.
  const reminderTz = (reminderTarget as ReminderPopupSession | null)?.timezone || "America/New_York";
  const eyebrowDate = reminderTarget
    ? new Date(reminderTarget.startDatetime).toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: reminderTz })
    : null;

  useEffect(() => {
    if (open) {
      setState({ kind: "idle" });
      setStep("form");
      setCalendarUrls(null);
    }
  }, [open, seriesSlug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    setIsNarrow(mq.matches);
    mq.addEventListener?.("change", onChange);
    const mqShort = window.matchMedia("(max-height: 700px)");
    const onShortChange = (e: MediaQueryListEvent) => setIsShort(e.matches);
    setIsShort(mqShort.matches);
    mqShort.addEventListener?.("change", onShortChange);
    return () => {
      mq.removeEventListener?.("change", onChange);
      mqShort.removeEventListener?.("change", onShortChange);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Shared ref-counted lock — never hand-roll body style saves here: two
  // overlapping modals restoring each other's snapshots froze the page
  // (2026-07-02 preview bug).
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => firstNameInputRef.current?.focus(), 220);
    return () => {
      window.clearTimeout(focusTimer);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  async function submit() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const trimmed = email.trim().toLowerCase();
      const trimmedName = firstName.trim();
      if (!EMAIL_RE.test(trimmed)) {
        setState({ kind: "error", message: "That email doesn't look right." });
        return;
      }
      if (!reminderTarget) {
        setState({ kind: "error", message: "Registration for this workshop just closed. Browse other dates?" });
        return;
      }

      setState({ kind: "loading" });
      const res = await fetch("/api/reminder/optin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          firstName: trimmedName || undefined,
          seriesSlug,
          eventId: reminderTarget.id,
          pageSlug,
          source: source ?? "exit_popup",
          ...getLeadAttribution(),
        }),
      });
      let body: { success?: boolean; message?: string; calendar?: CalendarUrls; metaEventId?: string | null };
      try {
        body = await res.json();
      } catch {
        throw new Error("Server didn't respond with JSON. If this is local dev, restart the server and try again.");
      }
      if (!res.ok || !body.success || !body.calendar) {
        throw new Error(body.message || "Couldn't schedule that. Try again?");
      }

      // Fire Meta pixel Lead event + GA4 generate_lead on Step 2 load.
      // Only fires for new leads (server returns metaEventId only then).
      // metaEventId matches the CAPI event_id for server↔browser deduplication.
      if (body.metaEventId) {
        const w = window as any;
        if (typeof w.fbq === "function") {
          w.fbq("track", "Lead", {
            content_name: "workshop_reminder_popup",
            content_category: "workshop",
          }, { eventID: body.metaEventId });
        }
        if (Array.isArray(w.dataLayer)) {
          w.dataLayer.push({
            event: "generate_lead",
            lead_source: "reminder_popup",
            series_slug: seriesSlug ?? undefined,
          });
        }
      }

      setCalendarUrls(body.calendar);
      setStep("confirm");
      setState({ kind: "idle" });
      onSubmitSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something broke. Try again?";
      setState({ kind: "error", message });
    } finally {
      submittingRef.current = false;
    }
  }

  /** Step 2 calendar-add — a real user click. Synchronous window.open is safe. */
  function addToCalendar(p: Provider) {
    if (!calendarUrls) return;
    if (p === "apple") {
      downloadIcs(`reminder.ics`, calendarUrls.ics);
    } else {
      window.open(p === "google" ? calendarUrls.google : calendarUrls.outlook, "_blank", "noopener");
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[114]"
            style={{ backgroundColor: "rgba(20, 19, 18, 0.75)" }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[115] flex items-center justify-center pointer-events-none"
            style={{
              paddingLeft: "max(12px, env(safe-area-inset-left))",
              paddingRight: "max(12px, env(safe-area-inset-right))",
              paddingTop: "max(12px, env(safe-area-inset-top))",
              paddingBottom: "max(12px, env(safe-area-inset-bottom))",
            }}
          >
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="reminder-popup-title"
              onClick={(e) => e.stopPropagation()}
              className="relative w-full bg-cream pointer-events-auto"
              style={{
                maxWidth: 640,
                maxHeight: "88dvh",
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                border: "var(--border-structural)",
              }}
            >
              <button
                type="button"
                onClick={onClose}
                aria-label="Close reminder dialog"
                className="absolute inline-flex items-center justify-center"
                style={{
                  top: 4,
                  right: 4,
                  width: 44,
                  height: 44,
                  color: step === "confirm" ? "var(--charcoal)" : "var(--cream)",
                  backgroundColor: step === "confirm" ? "transparent" : "rgba(20, 19, 18, 0.5)",
                  zIndex: 2,
                  border: 0,
                  cursor: "pointer",
                }}
              >
                <X size={20} />
              </button>

              {step === "form" && (
                <div
                  className="block w-full overflow-hidden"
                  // Short viewports get a fixed-height hero (not the full 850/392
                  // ratio) to keep the submit button reachable. 96px cropped the
                  // team to a head-only sliver; 132px + a 28% focal point shows
                  // them head-to-waist while costing only ~36px of extra height.
                  style={isShort ? { height: 132 } : { aspectRatio: "850 / 392" }}
                >
                  <img
                    src="/images/exit-intent-popover.jpg"
                    alt=""
                    width={850}
                    height={392}
                    loading="eager"
                    decoding="async"
                    className="block w-full h-full"
                    style={{ objectFit: "cover", objectPosition: "center 28%" }}
                  />
                </div>
              )}

              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  padding: step === "confirm" ? "32px 28px 44px" : isShort ? "18px 24px 20px" : "28px 28px 28px",
                }}
              >
                {step === "confirm" ? (
                  <div style={{ textAlign: "center", paddingTop: 24 }}>
                    <div aria-hidden="true" style={{ fontSize: 84, lineHeight: 1, marginBottom: 32 }}>📬</div>
                    <h3 id="reminder-popup-title" className="type-h3 text-charcoal" style={{ marginTop: 0, marginBottom: 18 }}>
                      Check your inbox now to confirm your email and access an exclusive discount code.
                    </h3>

                    <p
                      className="type-body text-charcoal/80 mx-auto"
                      style={{ marginBottom: 26, fontSize: 15, lineHeight: 1.6, maxWidth: 460 }}
                    >
                      We also emailed you a confirmation link. Click it to unlock{" "}
                      <strong style={{ color: "var(--terra)" }}>{REMINDER_REWARD_PERCENT}% off</strong> your ticket plus
                      free access to our AI recipe generator.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setStep("form");
                        setState({ kind: "idle" });
                        window.setTimeout(() => emailInputRef.current?.focus(), 50);
                      }}
                      className="type-body text-charcoal/75 hover:text-charcoal underline underline-offset-[4px] decoration-charcoal/40 hover:decoration-charcoal transition-colors"
                      style={{ fontSize: 15, fontWeight: 500, background: "none", border: 0, cursor: "pointer" }}
                    >
                      Used the wrong email? Go back
                    </button>

                    {/* Add-to-calendar — only after the API returned URLs. The
                        user clicks a provider to open Google / Outlook or
                        download an .ics for Apple. */}
                    {calendarUrls && (
                      <div
                        style={{
                          marginTop: 32,
                          paddingTop: 28,
                          borderTop: "1px solid rgba(20, 19, 18, 0.15)",
                        }}
                      >
                        <p
                          className="type-eyebrow text-charcoal/75"
                          style={{ textAlign: "center", marginBottom: 14 }}
                        >
                          Also add it to your calendar
                        </p>
                        <div
                          role="group"
                          aria-label="Add to calendar"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr",
                            gap: 0,
                            border: "1px solid var(--charcoal)",
                          }}
                        >
                          {(["google", "outlook", "apple"] as const).map((p, i) => {
                            const Icon =
                              p === "google" ? GoogleCalendarBrandIcon : p === "outlook" ? OutlookBrandIcon : AppleCalendarBrandIcon;
                            const label = p === "google" ? "Google" : p === "outlook" ? "Outlook" : "Apple";
                            return (
                              <button
                                key={p}
                                type="button"
                                onClick={() => addToCalendar(p)}
                                style={{
                                  minHeight: isNarrow ? 72 : 64,
                                  padding: isNarrow ? "10px 6px" : "12px 8px",
                                  backgroundColor: "rgba(20, 19, 18, 0.025)",
                                  color: "var(--charcoal)",
                                  fontFamily: "var(--font-mono)",
                                  fontSize: 11,
                                  letterSpacing: "0.12em",
                                  textTransform: "uppercase",
                                  borderRight: i < 2 ? "1px solid var(--charcoal)" : undefined,
                                  borderTop: 0,
                                  borderBottom: 0,
                                  borderLeft: 0,
                                  cursor: "pointer",
                                  display: "flex",
                                  flexDirection: isNarrow ? "column" : "row",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: isNarrow ? 6 : 10,
                                  borderRadius: 0,
                                  fontWeight: 500,
                                  transition: "background-color 180ms ease",
                                }}
                              >
                                <Icon size={isNarrow ? 28 : 32} />
                                <span>{label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Imminent session upsell — only when a session is < 24h away. */}
                    {countdown && countdownTarget && seriesSlug && (
                      <div
                        style={{
                          marginTop: 32,
                          paddingTop: 28,
                          borderTop: "1px solid rgba(20, 19, 18, 0.15)",
                        }}
                      >
                        <p className="type-body text-charcoal" style={{ marginBottom: 16, fontSize: 18, lineHeight: 1.5 }}>
                          You can still register for the workshop starting in{" "}
                          <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--terra)", fontSize: 20 }}>
                            {countdown.d > 0 ? `${countdown.d}d ` : ""}
                            {String(countdown.h).padStart(2, "0")}h {String(countdown.m).padStart(2, "0")}m {String(countdown.s).padStart(2, "0")}s
                          </span>
                        </p>
                        <a
                          href={`/workshops/${seriesSlug}?code=${REMINDER_REWARD_CODE}#tickets`}
                          className="btn btn-primary btn-lg w-full"
                          style={{ display: "block", textAlign: "center" }}
                        >
                          Grab a seat now
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Eyebrow: which series + the date we'll actually remind about. */}
                    {eyebrowDate && (
                      <p className="type-eyebrow text-charcoal/75 rp-eyebrow" style={{ textAlign: "center", marginBottom: 10 }}>
                        {/* Split into parts so a page skin can stack them or drop
                            the dash. Default rendering is byte-for-byte what it
                            was: "Series — Date" on one line. */}
                        <span className="rp-series">{shortTitle}</span>
                        <span className="rp-sep"> — </span>
                        <span className="rp-date">{eyebrowDate}</span>
                      </p>
                    )}
                    <h3 id="reminder-popup-title" className="type-h3 text-charcoal" style={{ marginTop: 0, marginBottom: isShort ? 14 : 22, textAlign: "center" }}>
                      Would you care for a single email reminder before registration closes?
                    </h3>

                    <label htmlFor="reminder-firstname" className="type-eyebrow text-charcoal/75 block" style={{ marginBottom: 8 }}>
                      First name
                    </label>
                    <input
                      ref={firstNameInputRef}
                      id="reminder-firstname"
                      type="text"
                      autoComplete="given-name"
                      autoCapitalize="words"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="Your first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") emailInputRef.current?.focus();
                      }}
                      style={{
                        width: "100%",
                        minHeight: 48,
                        padding: "12px 14px",
                        border: "1px solid var(--charcoal)",
                        fontFamily: "var(--font-sans)",
                        fontSize: 16,
                        color: "var(--charcoal)",
                        backgroundColor: "var(--cream)",
                        marginBottom: isShort ? 12 : 18,
                        borderRadius: 0,
                      }}
                    />

                    <label htmlFor="reminder-email" className="type-eyebrow text-charcoal/75 block" style={{ marginBottom: 8 }}>
                      Email
                    </label>
                    <input
                      ref={emailInputRef}
                      id="reminder-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submit();
                      }}
                      style={{
                        width: "100%",
                        minHeight: 48,
                        padding: "12px 14px",
                        border: "1px solid var(--charcoal)",
                        fontFamily: "var(--font-sans)",
                        fontSize: 16,
                        color: "var(--charcoal)",
                        backgroundColor: "var(--cream)",
                        marginBottom: isShort ? 12 : 18,
                        borderRadius: 0,
                      }}
                    />

                    <p className="type-body text-charcoal/80" style={{ marginBottom: isShort ? 16 : 26, fontSize: 14, lineHeight: 1.6 }}>
                      Confirm your email after this and we'll unlock{" "}
                      <strong style={{ color: "var(--terra)" }}>{REMINDER_REWARD_PERCENT}% off</strong> plus our free AI
                      recipe generator. No spam, no follow-ups.
                    </p>

                    {state.kind === "error" && (
                      <p role="alert" style={{ color: "var(--terra)", fontSize: 13, marginBottom: 12, fontFamily: "var(--font-sans)" }}>
                        {state.message}
                      </p>
                    )}

                    <button
                      type="button"
                      disabled={state.kind === "loading"}
                      onClick={() => submit()}
                      className="btn btn-cta btn-lg w-full"
                      style={{
                        minHeight: 52,
                        opacity: state.kind === "loading" ? 0.7 : 1,
                        cursor: state.kind === "loading" ? "wait" : "pointer",
                        fontFamily: "var(--font-sans)",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      {state.kind === "loading" ? "Scheduling…" : "Schedule my reminder"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
